import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRememberedTableState } from '@/lib/filter-memory/use-filter-memory';
import type {
  ColumnFilterOption,
  ColumnFilterState,
  ColumnRangeSpec,
  FilterScope,
  FilterableTHProps,
  SortDirection,
} from '@/components/ui/ColumnFilter';
import {
  EMPTY_FILTER,
  hasFilterInput,
  isFilterActive,
  numberConditionMatches,
  numberConditionText,
  parseStoredCondition,
  rangeBounds,
} from '@/components/ui/ColumnFilter';

export type {
  FilterScope,
  SortDirection,
  ColumnFilterState,
  ColumnNumberCondition,
  ColumnNumberOperator,
  ColumnRangeOperator,
  ColumnRangeSpec,
  ColumnRangeValue,
} from '@/components/ui/ColumnFilter';
export { numberConditionText } from '@/components/ui/ColumnFilter';

/** One active number condition, for a filter-chip row. */
export interface ActiveNumberCondition {
  /** The column key. */
  key: string;
  /** Chip text, e.g. `Calls > 100` or `Calls 10–50`. */
  text: string;
  /** Removes this column's condition only; its ticked values stay. */
  clear: () => void;
}

export interface ColumnSpec<T> {
  key: string;
  label: string;
  /** The cell's filterable value. null / '' / undefined all count as EMPTY. */
  get: (row: T) => string | number | null | undefined;
  /** Nicer label for a raw value (e.g. an id -> a name). Defaults to String(value). */
  format?: (value: string) => string;
  /** Present = the API can sort this column; the value is the API's sort key. */
  sortKey?: string;
  /** Defaults to 'page'. 'server' requires `param`. */
  scope?: FilterScope;
  /** Query-param name when scope is 'server'. */
  param?: string;
  /**
   * The complete option list, for a column whose values are a CLOSED SET the
   * API defines rather than something the data happens to contain.
   *
   * ⚠️ **WITHOUT THIS, A SERVER COLUMN CAN ONLY EVER OFFER WHAT IT HAS ALREADY
   * SEEN.** Options are accumulated out of loaded rows (`seenServerValues`), so
   * a value absent from the pages a person has looked at is a value they cannot
   * filter by — and the popover gives no hint that anything is missing. That is
   * tolerable for an open set like companies or users, where no list could ever
   * be complete. It is a real defect for an enum: the state you most want to
   * find is often the rare one, and rare is exactly what is missing from page 1.
   *
   * Supplying this REPLACES the accumulated list entirely, in the declared
   * order, with no counts — a count over loaded rows says nothing about a filter
   * that acts on the whole dataset, and the accumulated list has never carried
   * one either.
   *
   * ⚠️ **Order is preserved, NOT alphabetised**, unlike every other option list
   * here. A closed vocabulary usually has a meaning to its sequence — a
   * completeness scale, a pipeline — and sorting it by label destroys that while
   * looking tidier.
   *
   * ⚠️ **It is a claim that the API accepts every value listed.** An option the
   * server's enum does not hold sends a query key that comes back a 422 and
   * empties the table, which reads as "nothing matches" rather than as a bad
   * option — the same failure a wrong `sortKey` produces.
   *
   * Not retrofitted onto the other closed-enum columns in this app (`role` on
   * `/users` is the clearest one) — that would change what those popovers offer,
   * which is a behaviour change nobody asked for and not this control's to make.
   */
  options?: ColumnFilterOption[];
  /**
   * Opt in to sending SEVERAL selected values as one comma-separated param.
   *
   * ⚠️ Off by default, and it must stay that way. Only `/companies` and
   * `/companies/kanban` parse a list — measured against production, every other
   * endpoint answers 422 "expected number, received NaN" for `?stageId=1,2`,
   * on the list and the board alike. Turning this on globally would break the
   * filters on every other page.
   *
   * Without it, several selections fall back to filtering the LOADED PAGE only
   * (see `rawServerParams`), which is correct but silently narrower.
   */
  multi?: boolean;
  /**
   * Does this row match the ticked values? For a column whose cell is a SET.
   *
   * ⚠️ **`get` returns ONE scalar per row, so a set-valued column cannot answer
   * the page-scoped test honestly** — and it is reached more often than it
   * looks. A `server` column falls through to the page test whenever SEVERAL
   * values are ticked and the column is not `multi`, because the API takes one
   * id at a time and no query could carry them (`?groupId=1,2` is
   * "expected number, received NaN" on everything except `/companies`). At that
   * point the default test compares the ticked id against a joined cell like
   * `"1,3"`, matches nothing, and empties the table — a filter that reads as
   * "no users are in either group".
   *
   * Supplying this replaces only that comparison. The emptiness test still runs
   * off `get`, and a `multi` column or a single server selection still returns
   * early without testing anything, because the server already did.
   *
   * Additive: omitted behaves exactly as before.
   */
  matches?: (row: T, selected: readonly string[]) => boolean;
  /**
   * Right-aligns and tabular-aligns the header cell. **A DISPLAY FLAG AND
   * NOTHING ELSE** — see `range` below before reaching for it.
   */
  numeric?: boolean;
  /**
   * Declares this column NUMERIC and names the two bound params the API takes
   * for it. **Presence is the whole switch**: the popover drops the tickable
   * value list and offers comparison operators instead, exactly as `sortKey`'s
   * presence is the only thing that makes sort controls appear.
   *
   * ⚠️ **DELIBERATELY NOT `numeric`, WHICH MUST KEEP MEANING ONLY
   * "right-align".** The two look interchangeable and are not: every figure
   * column in this app is right-aligned, and almost none of them has a bound
   * pair on the wire. Measured on 2026-09-13, `numeric: true` is already
   * declared on `subTotal`, `discountTotal`, `taxTotal` (three columns with no
   * min/max params at all), on `id` on `/users`, and on **Actions** columns —
   * so overloading it would replace those value lists with an operator UI whose
   * only possible outcome is a query that goes nowhere. **Grep `numeric: true`
   * before ever reconsidering this**; the count has only one direction to go.
   *
   * Both params are **INCLUSIVE**, which is what the operator labels are worded
   * against, and **a NULL row matches NEITHER of them** — so the pair does not
   * partition the list, and Is empty / Is not empty remain the only way to
   * reach an unrecorded figure. `signed` and `decimal` are per column and are
   * documented on `ColumnRangeSpec`; getting `signed` wrong on `pending` hides
   * every overdelivered row.
   *
   * The pairs the API has (2026-09-13): `amountMin`/`amountMax` on `/deals`,
   * `/quotes` and `/invoices`; `pendingMin`/`pendingMax`,
   * `promisedMin`/`promisedMax`, `completedMin`/`completedMax` on
   * `/client-deliverables`; `capacityMin`/`capacityMax` on `/projects`.
   * A DATE pair sets `date: true` (2026-10-02: `lastNoteAfter`/`lastNoteBefore`
   * on `/companies`) — the bounds are then `YYYY-MM-DD` strings, passed through
   * exactly as typed, and `signed`/`decimal` are ignored.
   * ⚠️ **A pair this app invents is not a 422 on every one of those** — an
   * unknown key is silently stripped by some of these modules, so the filter
   * looks like it worked. Check the endpoint's schema, not the response.
   *
   * ⚠️ **The bounds are server-side WHATEVER `scope` says**, and a range column
   * needs no `scope: 'server'` and no `param`: there is no page-scoped numeric
   * filter here, because narrowing one loaded page by a threshold answers a
   * question about 100 rows while looking like an answer about all of them.
   * `scope` still governs the (unused) value list, and `emptiness` is still
   * page-scoped, as it is on every column.
   *
   * ⚠️ **`options`, `multi` and `matches` are ignored on a range column** — it
   * has no value list for them to act on. Do not combine them.
   */
  range?: ColumnRangeSpec & { minParam: string; maxParam: string };
  /**
   * `'number'` = a PLAIN-NUMBER column filtered on the client with conditions
   * (>, ≥, <, ≤, =, between; owner 2026-10-29, ported from the LAE app). `get`
   * must return a `number`, or null/undefined. The value list stays, and a
   * condition ANDs with it; a null/undefined cell never matches a condition.
   *
   * ⚠️ **Page-scoped**, like every value list here: it tests the loaded rows
   * only. Meant for tables that hold their whole dataset (LAE productivity).
   *
   * ⛔ **Never on a money column.** A money cell is a decimal string, and a
   * string cell never matches a condition (no parsing). Use `range` for a
   * server-bounded figure; `kind` is ignored when `range` is present.
   *
   * Omitted = exactly the behaviour before it existed.
   */
  kind?: 'number';
}

/** A `kind: 'number'` column that actually gets the condition editor. */
function takesCondition<T>(column: ColumnSpec<T>): boolean {
  return column.kind === 'number' && !column.range;
}

export interface TableFilters<T> {
  /** Rows after page-scoped filtering. Render these, not the raw rows. */
  visibleRows: T[];
  /**
   * Params for server-scoped columns AND for every numeric column's bounds, to
   * merge into the list request.
   *
   * ⚠️ **A page that declares a `range` and does not merge these into its fetch
   * has built a control that silently does nothing** — the funnel fills, the
   * operator is remembered, and the table never narrows. There is nothing this
   * hook can do about that from here.
   */
  serverParams: Record<string, string>;
  /** Current server sort, or null. */
  sort: { key: string; direction: SortDirection } | null;
  /** Spread onto FilterableTH: <FilterableTH {...headerFor('name')} /> */
  headerFor: (key: string) => FilterableTHProps;
  activeCount: number;
  clearAll: () => void;
  /**
   * Every `kind: 'number'` column whose condition is narrowing something, in
   * column order. Already counted in `activeCount`; `clearAll` clears them too.
   */
  activeConditions: ActiveNumberCondition[];
}

/**
 * `null` for anything that counts as empty, otherwise the value as a string.
 *
 * A whitespace-only cell is empty; `0` and `"0.00"` are not. That last one is
 * load-bearing for the catalog: an unpriced product has `unitPrice: null` while
 * a genuinely free one has `"0.00"`, and collapsing the two is the exact bug
 * CLAUDE.md warns about.
 */
function normalise(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw);
  return text.trim() === '' ? null : text;
}

/*
 * Filter memory: what comes back from the account is not this build's to
 * trust, so anything unrecognised is dropped — unknown columns, malformed
 * states, a sort on a key no column declares. Junk degrades to "nothing
 * remembered", never to a throw.
 */
function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function rememberedFilters<T>(
  blob: unknown,
  columns: ColumnSpec<T>[],
): Record<string, ColumnFilterState> {
  if (blob === null || typeof blob !== 'object') return {};
  const raw = (blob as { f?: unknown }).f;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, ColumnFilterState> = {};
  for (const column of columns) {
    const state = (raw as Record<string, unknown>)[column.key];
    if (state === null || typeof state !== 'object') continue;
    const { selected, emptiness, range, condition } = state as Record<string, unknown>;
    if (!isStringArray(selected)) continue;
    if (emptiness !== 'any' && emptiness !== 'empty' && emptiness !== 'notEmpty') continue;
    const next: ColumnFilterState = { selected, emptiness };
    if (column.range && range !== null && typeof range === 'object') {
      const { operator, min, max } = range as Record<string, unknown>;
      if (
        (operator === 'atLeast' || operator === 'atMost' || operator === 'equals' || operator === 'between') &&
        typeof min === 'string' &&
        typeof max === 'string'
      ) {
        next.range = { operator, min, max };
      }
    }
    // Absent on every state stored before 2026-10-29, and junk parses to none.
    if (takesCondition(column)) {
      const parsed = parseStoredCondition(condition);
      if (parsed) next.condition = parsed;
    }
    if (hasFilterInput(next)) out[column.key] = next;
  }
  return out;
}

function rememberedSort<T>(
  blob: unknown,
  columns: ColumnSpec<T>[],
): { key: string; direction: SortDirection } | null {
  if (blob === null || typeof blob !== 'object') return null;
  const raw = (blob as { s?: unknown }).s;
  if (raw === null || typeof raw !== 'object') return null;
  const { key, direction } = raw as Record<string, unknown>;
  if (typeof key !== 'string' || (direction !== 'asc' && direction !== 'desc')) return null;
  if (!columns.some((column) => column.sortKey === key)) return null;
  return { key, direction };
}

/** Does a stored blob name a sort at all (valid for these columns or not)? */
function hasStoredSort(blob: unknown): boolean {
  if (blob === null || typeof blob !== 'object') return false;
  const raw = (blob as { s?: unknown }).s;
  return raw !== null && typeof raw === 'object';
}

function byLabel(a: ColumnFilterOption, b: ColumnFilterOption) {
  // `numeric` so "Intake 2" sorts before "Intake 10" rather than after it.
  return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Column filtering for a server-paginated table.
 *
 * The rule the whole thing exists to enforce: **a column may only offer what
 * the API can actually do.**
 *
 * - Distinct values come from `rows`, i.e. the loaded page. A `'page'` column
 *   therefore filters the loaded page and says so in its popover.
 * - A `'server'` column emits a query param instead. The hook never applies its
 *   selection locally — the server has already done it — and never offers
 *   multi-select, because the params these map to (`ownerId`, `companyId`,
 *   `cycleId`, `active`, …) each take one value.
 * - `sortKey` is the only thing that makes sort controls appear. **This hook
 *   never sorts rows itself**: reordering one page while implying the whole
 *   table is ordered is worse than no sort at all.
 * - A `range` column is NUMERIC: its popover offers comparison operators over
 *   the API's own min/max pair instead of a list of values, and those bounds are
 *   always server-side. **This hook never compares a number itself**, for the
 *   same reason it never sorts — and a money bound is an exact decimal string
 *   that is never parsed.
 *
 * Wiring on the page side:
 *
 * ```tsx
 * const filters = useTableFilters(rows, columns);
 * useEffect(() => { load(); }, [page, filters.serverParams, filters.sort]);
 * // ...and reset to page 1 whenever serverParams or sort changes, exactly as
 * // the search box already does.
 * <FilterableTH {...filters.headerFor('name')} />
 * {filters.visibleRows.map(...)}
 * ```
 *
 * `serverParams` keeps a stable identity while its contents are unchanged, so
 * listing it in a fetch effect's deps is safe. `sort` is state, so it is stable
 * too. `sort.key` is the API's sort key (the column's `sortKey`), not the
 * column key — it goes straight into the request.
 */
export function useTableFilters<T>(
  rows: T[],
  columns: ColumnSpec<T>[],
  /**
   * `remember: false` opts a table out of filter memory (owner 2026-10-16).
   * By default every table remembers its funnels and sort on the account, per
   * pathname (record pages per record TYPE) and column set or `memoryId`.
   */
  options: {
    remember?: boolean;
    /**
     * A stable name for this table's memory entry (owner 2026-10-23). Give
     * one when the column SET can change during the table's life — columns
     * that arrive after a read (`TopMoversPanel`'s cycle columns), or that
     * depend on permissions — so the key no longer moves with them. Unique
     * per page; lowercase letters, digits and `-`. Omitted = keyed by a hash
     * of the column keys, as before.
     */
    memoryId?: string;
  } = {},
): TableFilters<T> {
  const columnSignature = columns.map((column) => column.key).join('|');
  const memory = useRememberedTableState(
    columnSignature,
    options.remember !== false,
    options.memoryId,
  );
  const [filters, setFilters] = useState<Record<string, ColumnFilterState>>(
    () => rememberedFilters(memory.initial, columns),
  );
  const [sort, setSort] = useState<{ key: string; direction: SortDirection } | null>(() =>
    rememberedSort(memory.initial, columns),
  );

  /*
   * Store every change, a clear included (`''`), so a cleared funnel stays
   * cleared. The first pass only records the baseline: restoring is not a
   * change, and a table that merely mounted never writes.
   *
   * On a link arrival that names no sort, the remembered SORT was restored
   * (`mayRestoreSort`) and is part of this state, so the first funnel change
   * writes it back unchanged rather than overwriting it with "no sort".
   */
  const memorySignature = JSON.stringify({ f: filters, s: sort });
  const memoryBaseline = useRef(memorySignature);
  /** The person changed a funnel or the sort since mount. */
  const memoryTouched = useRef(false);
  const { remember } = memory;
  useEffect(() => {
    if (memoryBaseline.current === memorySignature) return;
    memoryBaseline.current = memorySignature;
    memoryTouched.current = true;
    const empty = Object.keys(filters).length === 0 && sort === null;
    remember(empty ? '' : memorySignature);
  }, [memorySignature, filters, sort, remember]);

  /*
   * Late memory (2026-10-23 — sorts were lost here). The mount read can come
   * too early: the account copy lands after mount on a page `<FilterMemory>`
   * did not hold back long enough, another tab writes, or the columns that
   * validate the stored sort (and pick the key) arrive after a read. While the
   * person has not touched this table, whatever memory now holds — re-validated
   * against the CURRENT columns — is applied. Adopting is not a change: the
   * baseline moves with it, so nothing is written back.
   */
  const latest = memory.latest;
  useEffect(() => {
    if (memoryTouched.current || latest === undefined) return;
    const nextFilters = rememberedFilters(latest, columns);
    const nextSort = rememberedSort(latest, columns);
    const nextSignature = JSON.stringify({ f: nextFilters, s: nextSort });
    if (nextSignature === memoryBaseline.current) return;
    // A stored sort the current columns cannot drive yet stays pending, rather
    // than clearing a sort that is already applied.
    if (nextSort === null && sort !== null && hasStoredSort(latest)) return;
    memoryBaseline.current = nextSignature;
    // oxlint-disable-next-line react/set-state-in-effect
    setFilters(nextFilters);
    // oxlint-disable-next-line react/set-state-in-effect
    setSort(nextSort);
  }, [latest, columns, sort]);

  /*
   * Every value ever seen for a server-scoped column.
   *
   * Once such a filter is applied the next response contains only matching
   * rows, so a value list derived from `rows` alone would collapse to the one
   * value already chosen — the filter would be one-way, impossible to switch
   * without reloading the page. State rather than a ref so nothing is mutated
   * during render; the updater returns `prev` unchanged when there is nothing
   * new, which is what stops the effect from looping on an inline `columns`.
   */
  const [seenServerValues, setSeenServerValues] = useState<Record<string, Record<string, string>>>(
    {},
  );

  useEffect(() => {
    // The lint rule is right in general and wrong here: this genuinely is an
    // accumulation across successive server responses, which cannot be derived
    // from the current render's props. The updater returns `prev` when nothing
    // is new, so it settles after one extra pass per load and never cascades.
    // oxlint-disable-next-line react/set-state-in-effect
    setSeenServerValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const column of columns) {
        if ((column.scope ?? 'page') !== 'server') continue;
        // A range column renders no value list, so accumulating its distinct
        // figures would build a list nothing can show.
        if (column.range) continue;
        const bucket = { ...(next[column.key] ?? {}) };
        let bucketChanged = false;
        for (const row of rows) {
          const cell = normalise(column.get(row));
          if (cell === null) continue;
          const label = column.format?.(cell) ?? cell;
          if (bucket[cell] === label) continue;
          bucket[cell] = label;
          bucketChanged = true;
        }
        if (!bucketChanged) continue;
        next[column.key] = bucket;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [rows, columns]);

  const optionsByKey = useMemo(() => {
    const result: Record<string, ColumnFilterOption[]> = {};

    for (const column of columns) {
      const labelFor = (raw: string) => column.format?.(raw) ?? raw;

      /*
       * A numeric column has no option list at all — the popover offers
       * operators over a min/max pair instead. Emptied here rather than left to
       * the panel's own branch, so a stray `options` on a range column cannot
       * sit in state describing a control that is not rendered.
       */
      if (column.range) {
        result[column.key] = [];
        continue;
      }

      /*
       * A declared list wins over anything derived, for a page-scoped column as
       * well as a server one: the point is that the vocabulary is CLOSED and
       * known in advance, which is true whichever side does the filtering.
       *
       * Deliberately NOT sorted — see `ColumnSpec.options`.
       */
      if (column.options) {
        result[column.key] = column.options;
        continue;
      }

      if ((column.scope ?? 'page') === 'server') {
        // No counts here: the number of loaded rows holding a value says
        // nothing about a filter that acts on the whole dataset.
        result[column.key] = Object.entries(seenServerValues[column.key] ?? {})
          .map(([value, label]) => ({ value, label }))
          .sort(byLabel);
        continue;
      }

      const counts = new Map<string, number>();
      for (const row of rows) {
        const cell = normalise(column.get(row));
        if (cell === null) continue;
        counts.set(cell, (counts.get(cell) ?? 0) + 1);
      }
      const list = Array.from(counts, ([value, count]) => ({
        value,
        label: labelFor(value),
        count,
      }));
      // A number column lists its figures numerically, as LAE does ("-5"
      // before "3"); a value that does not read as a number keeps label order.
      result[column.key] = takesCondition(column)
        ? list.sort((a, b) => {
            const left = Number(a.value);
            const right = Number(b.value);
            if (Number.isFinite(left) && Number.isFinite(right)) return left - right || byLabel(a, b);
            return byLabel(a, b);
          })
        : list.sort(byLabel);
    }

    return result;
  }, [rows, columns, seenServerValues]);

  const visibleRows = useMemo(
    () =>
      rows.filter((row) =>
        columns.every((column) => {
          const state = filters[column.key];
          if (!state) return true;
          const cell = normalise(column.get(row));

          // Emptiness is always page-scoped — no endpoint in this API exposes an
          // is-null filter — which is why a 'server' column's footnote says so.
          if (state.emptiness === 'empty' && cell !== null) return false;
          if (state.emptiness === 'notEmpty' && cell === null) return false;

          // The number condition ANDs with everything else, and is tested on
          // the raw `get` value: only a finite number can match it.
          if (takesCondition(column) && !numberConditionMatches(state.condition, column.get(row))) {
            return false;
          }

          if (state.selected.length === 0) return true;

          /*
           * Anything the SERVER already filtered must not be filtered again
           * here — re-applying it would test the response against a label the
           * server never used.
           *
           * That is every selection on a `multi` column (they all went out as
           * one comma-separated param) and a single selection on any other
           * server column. Only a non-multi column with SEVERAL ticked reaches
           * the row test, because that is the case where no query could carry
           * them and the loaded page is genuinely all that was narrowed.
           *
           * ⚠️ The `multi` half is not cosmetic. `get` returns ONE scalar per
           * row, so a column whose value is a SET cannot answer "does this row
           * match?" honestly — a company holding four regions has no single
           * cell value, and re-testing it here would drop rows the server had
           * correctly returned.
           */
          const columnScope = column.scope ?? 'page';
          if (columnScope === 'server' && (column.multi || state.selected.length === 1)) {
            return true;
          }

          // A set-valued cell answers for itself; see `ColumnSpec.matches`.
          if (column.matches) return column.matches(row, state.selected);

          return cell !== null && state.selected.includes(cell);
        }),
      ),
    [rows, columns, filters],
  );

  const rawServerParams: Record<string, string> = {};
  for (const column of columns) {
    /*
     * A numeric column emits its two INCLUSIVE bounds and nothing else.
     *
     * ⛔ **`rangeBounds` is the single gate between a bound box and the query
     * string, and it returns `null` for an empty or unusable box so the key is
     * OMITTED.** Sending it blank is the failure this is built to prevent:
     * `?pendingMin=` coerces to a real bound of zero on the older pairs — a
     * `200` with a silently narrowed list — and on `capacity` a zero floor drops
     * every uncapped project, which is four in five of them.
     *
     * ⚠️ **The value goes through as the STRING it was typed as.** Money bounds
     * are exact decimals; nothing here parses or reformats one.
     *
     * This runs regardless of `scope`, because the bounds are always the
     * server's to apply — see `ColumnSpec.range`.
     */
    if (column.range) {
      const bounds = rangeBounds(filters[column.key]?.range, column.range);
      if (bounds.min !== null) rawServerParams[column.range.minParam] = bounds.min;
      if (bounds.max !== null) rawServerParams[column.range.maxParam] = bounds.max;
      // No value list, so nothing below this point can apply to it.
      continue;
    }
    if ((column.scope ?? 'page') !== 'server') continue;
    // A 'server' column with no `param` is a call-site slip; emitting the column
    // key as a param name would be silently stripped by the API.
    if (!column.param) continue;
    /*
     * A `multi` column sends every selection as one comma-separated param, so
     * the filter is applied by the SERVER across all pages.
     *
     * Without `multi`, only a single selection can be sent: those params take
     * one value, and with several chosen there is no query to express — the
     * hook filters the loaded page instead (see `visibleRows`). Sending the
     * first of several would silently ignore the rest, which is worse than not
     * sending at all.
     */
    const selected = (filters[column.key]?.selected ?? []).filter(
      (value) => value !== undefined && value !== '',
    );
    if (selected.length === 0) continue;
    if (column.multi) {
      rawServerParams[column.param] = selected.join(',');
      continue;
    }
    if (selected.length !== 1) continue;
    const chosen = selected[0];
    if (chosen === undefined) continue;
    rawServerParams[column.param] = chosen;
  }
  const serverParamsSignature = JSON.stringify(rawServerParams);
  // Stable identity while the contents are unchanged: a fresh object every
  // render would make any fetch effect that depends on it loop forever.
  const serverParams = useMemo(
    () => JSON.parse(serverParamsSignature) as Record<string, string>,
    [serverParamsSignature],
  );

  /*
   * ⚠️ **`isFilterActive` is imported, not reimplemented.** This was spelled out
   * inline here and again in the drop-to-default test below, and the funnel in
   * `ColumnFilter` had a third copy — three spellings of "is this column
   * filtered" that agreed only because nothing had been added to the state since
   * they were written. The range is exactly the thing they would have disagreed
   * about, and the symptom is a filled funnel over an unfiltered table.
   */
  const activeCount = columns.reduce((total, column) => {
    const state = filters[column.key];
    if (!state) return total;
    return isFilterActive(state) ? total + 1 : total;
  }, 0);

  /*
   * Deliberately not memoised. Call sites pass `columns` as an inline literal,
   * so every dependency list containing it is already fresh each render — a
   * `useCallback` here would cost a comparison and hold nothing. `headerFor` is
   * only ever called during render.
   */
  const headerFor = (key: string): FilterableTHProps => {
    const column = columns.find((candidate) => candidate.key === key);
    // A plain header rather than a throw: a mistyped key should not take the
    // whole page down.
    if (!column) return { label: key };

    const scope = column.scope ?? 'page';
    const sortKey = column.sortKey;

    return {
      label: column.label,
      numeric: column.numeric,
      filter: {
        label: column.label,
        options: optionsByKey[key] ?? [],
        // Present only for a numeric column — this is what swaps the value list
        // for the operator controls. The param names are not passed down: the
        // panel never builds a request.
        range: column.range,
        // Present only for a plain-number column: adds the condition editor.
        kind: takesCondition(column) ? 'number' : undefined,
        value: filters[key] ?? EMPTY_FILTER,
        onChange: (next) =>
          setFilters((current) => {
            /*
             * Drop the entry entirely when it returns to the default, so
             * `activeCount` and `clearAll` stay honest.
             *
             * ⚠️ **`hasFilterInput`, not `isFilterActive`, and the difference is
             * deliberate.** A chosen operator with both boxes still empty
             * filters nothing — so it must not be counted — but it has to be
             * remembered, or the state is dropped and the operator snaps back to
             * "At least" under somebody who has just picked "At most" and is
             * about to type into it.
             */
            if (!hasFilterInput(next)) {
              if (!(key in current)) return current;
              const rest: Record<string, ColumnFilterState> = {};
              for (const [otherKey, state] of Object.entries(current)) {
                if (otherKey !== key) rest[otherKey] = state;
              }
              return rest;
            }
            return { ...current, [key]: next };
          }),
        // Present only when the API can sort this column — this is what stops
        // the sort UI rendering at all everywhere else.
        sort: sortKey
          ? {
              direction: sort?.key === sortKey ? sort.direction : null,
              onSort: (direction) => setSort({ key: sortKey, direction }),
            }
          : undefined,
        scope,
        pageRowCount: rows.length,
        /*
         * Every column is multi-select. A server column's param takes one value
         * (`?ownerId=3,7` coerces to NaN and 422s), so with several ticked the
         * query is not sent and the loaded page is filtered instead. The
         * default footnote cannot describe that, so say it explicitly.
         */
        /*
         * A `multi` column sends every tick as one param, so several values
         * still query the whole table — the opposite of the note below, and
         * saying the wrong one would tell somebody their filter is narrower
         * than it is.
         */
        scopeNote:
          scope !== 'server' || (filters[key]?.selected.length ?? 0) <= 1
            ? undefined
            : column.multi
              ? // Keeps the emptiness caveat the default note carries. Emptiness
                // is applied BEFORE the server-column early return in
                // `visibleRows`, so it is page-scoped on a multi column too —
                // dropping that sentence would overstate what the filter did.
                `All selected values are sent together, so this still queries the whole table. Is empty / Is not empty apply only to the ${rows.length === 1 ? '1 row' : `${rows.length} rows`} loaded here.`
              : `Several values selected — the API filters this column by one value at a time, so this now filters the ${rows.length === 1 ? '1 row' : `${rows.length} rows`} on this page. Pick a single value to query the whole table.`,
      },
    };
  };

  // Deliberately leaves `sort` alone: sorting is a server query, and silently
  // reordering the table from a button labelled "clear filters" is a surprise.
  const clearAll = useCallback(() => setFilters({}), []);

  const activeConditions: ActiveNumberCondition[] = [];
  for (const column of columns) {
    if (!takesCondition(column)) continue;
    const text = numberConditionText(column.label, filters[column.key]?.condition);
    if (text === null) continue;
    const key = column.key;
    activeConditions.push({
      key,
      text,
      clear: () =>
        setFilters((current) => {
          const state = current[key];
          if (!state?.condition) return current;
          // Rebuilt without `condition`, then dropped if nothing else is left.
          const next: ColumnFilterState = { selected: state.selected, emptiness: state.emptiness };
          if (state.range) next.range = state.range;
          if (hasFilterInput(next)) return { ...current, [key]: next };
          const rest: Record<string, ColumnFilterState> = {};
          for (const [otherKey, other] of Object.entries(current)) {
            if (otherKey !== key) rest[otherKey] = other;
          }
          return rest;
        }),
    });
  }

  return { visibleRows, serverParams, sort, headerFor, activeCount, clearAll, activeConditions };
}
